import React, { useState, useEffect, useRef } from 'react';

// ─── StickerPicker Component ────────────────────────────────────────────────
export function StickerPicker({ onSelect, onClose, darkMode }) {
  const [packs, setPacks] = useState([]);
  const [activePack, setActivePack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    fetchPacks();
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  async function fetchPacks() {
    try {
      const token = localStorage.getItem('gc_token');
      const res = await fetch('/api/stickers/packs', {
        headers: { 'Authorization': 'Bearer ' + token, 'Accept': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not load stickers.'); setLoading(false); return; }
      setPacks(data);
      if (data.length > 0) setActivePack(data[0].id);
    } catch (e) { setError('Failed to load stickers.'); }
    setLoading(false);
  }

  const bg     = darkMode ? '#1a1a2e' : '#ffffff';
  const border  = darkMode ? '#2d2d4e' : '#e5e7eb';
  const tabBg   = darkMode ? '#16213e' : '#f3f4f6';
  const tabActive = darkMode ? '#e91e8c' : '#e91e8c';
  const text    = darkMode ? '#f1f1f1' : '#111827';
  const subtext = darkMode ? '#9ca3af' : '#6b7280';

  const currentStickers = activePack
    ? (packs.find(p => p.id === activePack) || {}).stickers || []
    : [];

  return (
    <div ref={ref} style={{
      position: 'absolute', bottom: '60px', left: '0',
      width: '320px', maxHeight: '380px',
      background: bg, border: '1px solid ' + border,
      borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden'
    }}>

      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + border,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: '14px', color: text }}>Stickers</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none',
          cursor: 'pointer', fontSize: '18px', color: subtext, lineHeight: 1 }}>x</button>
      </div>

      {/* Loading / Error */}
      {loading && <div style={{ padding: '32px', textAlign: 'center', color: subtext }}>Loading stickers...</div>}
      {error && <div style={{ padding: '24px', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔒</div>
        <div style={{ color: subtext, fontSize: '13px' }}>{error}</div>
      </div>}

      {!loading && !error && packs.length === 0 && (
        <div style={{ padding: '32px', textAlign: 'center', color: subtext, fontSize: '13px' }}>No sticker packs available yet.</div>
      )}

      {!loading && !error && packs.length > 0 && (<>

        {/* Pack Tabs */}
        <div style={{ display: 'flex', overflowX: 'auto', padding: '8px 8px 0',
          gap: '6px', background: tabBg, borderBottom: '1px solid ' + border }}>
          {packs.map(pack => (
            <button key={pack.id} onClick={() => setActivePack(pack.id)}
              style={{
                padding: '6px 12px', borderRadius: '20px', border: 'none',
                cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                whiteSpace: 'nowrap',
                background: activePack === pack.id ? tabActive : 'transparent',
                color: activePack === pack.id ? '#ffffff' : subtext,
                transition: 'all 0.2s'
              }}>
              {pack.cover_image_url
                ? <img src={pack.cover_image_url} alt={pack.name} style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle', borderRadius: '4px' }} />
                : null}
              {pack.name}
            </button>
          ))}
        </div>

        {/* Sticker Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '8px', padding: '12px', overflowY: 'auto', flex: 1 }}>
          {currentStickers.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', color: subtext, fontSize: '13px', padding: '24px 0' }}>No stickers in this pack yet.</div>
          )}
          {currentStickers.map(sticker => (
            <button key={sticker.id} onClick={() => { onSelect(sticker); onClose(); }}
              title={sticker.name}
              style={{
                background: 'none', border: '2px solid transparent', borderRadius: '10px',
                cursor: 'pointer', padding: '4px',
                transition: 'border-color 0.15s, transform 0.15s',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = tabActive; e.currentTarget.style.transform = 'scale(1.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'scale(1)'; }}>
              <img src={sticker.url} alt={sticker.name}
                style={{ width: '56px', height: '56px', objectFit: 'contain', borderRadius: '6px' }} />
            </button>
          ))}
        </div>

      </>)}
    </div>
  );
}

// ─── StickerMessage — renders a sticker in chat/post/comment ────────────────
export function StickerMessage({ sticker, darkMode }) {
  const bg = darkMode ? '#1a1a2e' : '#f0f0f0';
  return (
    <div style={{ display: 'inline-block' }}>
      <img
        src={sticker.url || sticker.sticker_url}
        alt={sticker.name || 'sticker'}
        style={{
          width: '120px', height: '120px', objectFit: 'contain',
          borderRadius: '12px', background: bg, padding: '6px',
          display: 'block'
        }}
      />
    </div>
  );
}

// ─── StickerPickerInline — for use inside posts/comments (no absolute pos) ──
export function StickerPickerInline({ onSelect, onClose, darkMode }) {
  const [packs, setPacks] = useState([]);
  const [activePack, setActivePack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchPacks(); }, []);

  async function fetchPacks() {
    try {
      const token = localStorage.getItem('gc_token');
      const res = await fetch('/api/stickers/packs', {
        headers: { 'Authorization': 'Bearer ' + token, 'Accept': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not load stickers.'); setLoading(false); return; }
      setPacks(data);
      if (data.length > 0) setActivePack(data[0].id);
    } catch (e) { setError('Failed to load stickers.'); }
    setLoading(false);
  }

  const bg      = darkMode ? '#1a1a2e' : '#ffffff';
  const border  = darkMode ? '#2d2d4e' : '#e5e7eb';
  const tabBg   = darkMode ? '#16213e' : '#f3f4f6';
  const tabActive = '#e91e8c';
  const text    = darkMode ? '#f1f1f1' : '#111827';
  const subtext = darkMode ? '#9ca3af' : '#6b7280';

  const currentStickers = activePack
    ? (packs.find(p => p.id === activePack) || {}).stickers || []
    : [];

  return (
    <div style={{
      width: '100%', maxHeight: '320px',
      background: bg, border: '1px solid ' + border,
      borderRadius: '16px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      zIndex: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden'
    }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid ' + border,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 700, fontSize: '13px', color: text }}>Stickers</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none',
          cursor: 'pointer', fontSize: '16px', color: subtext }}>✕</button>
      </div>

      {loading && <div style={{ padding: '20px', textAlign: 'center', color: subtext, fontSize: 13 }}>Loading...</div>}
      {error && <div style={{ padding: '20px', textAlign: 'center', color: subtext, fontSize: 13 }}>🔒 {error}</div>}
      {!loading && !error && packs.length === 0 && (
        <div style={{ padding: '20px', textAlign: 'center', color: subtext, fontSize: 12 }}>No sticker packs yet.</div>
      )}

      {!loading && !error && packs.length > 0 && (<>
        <div style={{ display: 'flex', overflowX: 'auto', padding: '6px 8px 0',
          gap: '6px', background: tabBg, borderBottom: '1px solid ' + border }}>
          {packs.map(pack => (
            <button key={pack.id} onClick={() => setActivePack(pack.id)}
              style={{ padding: '5px 10px', borderRadius: '20px', border: 'none',
                cursor: 'pointer', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
                background: activePack === pack.id ? tabActive : 'transparent',
                color: activePack === pack.id ? '#fff' : subtext }}>
              {pack.name}
            </button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '6px', padding: '10px', overflowY: 'auto', flex: 1 }}>
          {currentStickers.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', color: subtext, fontSize: 12, padding: '16px 0' }}>No stickers in this pack.</div>
          )}
          {currentStickers.map(sticker => (
            <button key={sticker.id} onClick={() => { onSelect(sticker); onClose(); }}
              title={sticker.name}
              style={{ background: 'none', border: '2px solid transparent', borderRadius: '8px',
                cursor: 'pointer', padding: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = tabActive; e.currentTarget.style.transform = 'scale(1.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'scale(1)'; }}>
              <img src={sticker.url} alt={sticker.name}
                style={{ width: '44px', height: '44px', objectFit: 'contain', borderRadius: '4px' }} />
            </button>
          ))}
        </div>
      </>)}
    </div>
  );
}

// ─── StickerSheet — WhatsApp-style bottom sheet picker ──────────────────────
export function StickerSheet({ onSelect, onClose, darkMode }) {
  const [packs, setPacks] = useState([]);
  const [activePack, setActivePack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchPacks(); }, []);

  async function fetchPacks() {
    try {
      const token = localStorage.getItem('gc_token');
      const res = await fetch('/api/stickers/packs', {
        headers: { 'Authorization': 'Bearer ' + token, 'Accept': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Stickers are for Premium & Elite members.'); setLoading(false); return; }
      setPacks(data);
      if (data.length > 0) setActivePack(data[0].id);
    } catch (e) { setError('Failed to load stickers.'); }
    setLoading(false);
  }

  const bg     = darkMode ? '#16213e' : '#ffffff';
  const border = darkMode ? '#2d2d4e' : '#e5e7eb';
  const text   = darkMode ? '#f1f1f1' : '#111827';
  const sub    = darkMode ? '#9ca3af' : '#6b7280';
  const active = '#e91e8c';

  const currentStickers = activePack
    ? (packs.find(p => p.id === activePack) || {}).stickers || []
    : [];

  return (
    <div style={{
      width: '100%',
      background: bg,
      borderTop: '2px solid ' + border,
      borderRadius: '16px 16px 0 0',
      paddingBottom: 8,
    }}>
      {/* Pack name tabs */}
      {!loading && !error && packs.length > 0 && (
        <div style={{
          display: 'flex', gap: 0, overflowX: 'auto',
          borderBottom: '1px solid ' + border,
          padding: '0 12px',
        }}>
          {packs.map(pack => (
            <button key={pack.id} onClick={() => setActivePack(pack.id)}
              style={{
                padding: '10px 16px', background: 'none', border: 'none',
                borderBottom: activePack === pack.id ? '3px solid ' + active : '3px solid transparent',
                color: activePack === pack.id ? active : sub,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                whiteSpace: 'nowrap', transition: 'all 0.15s'
              }}>
              {pack.name}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading && <div style={{ padding: '24px', textAlign: 'center', color: sub, fontSize: 13 }}>Loading stickers...</div>}
      {error && <div style={{ padding: '24px', textAlign: 'center', color: sub, fontSize: 13 }}>🔒 {error}</div>}
      {!loading && !error && packs.length === 0 && (
        <div style={{ padding: '24px', textAlign: 'center', color: sub, fontSize: 12 }}>No sticker packs yet.</div>
      )}

      {/* Sticker grid */}
      {!loading && !error && currentStickers.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8,
          padding: '12px 12px 4px', overflowY: 'auto', maxHeight: 200
        }}>
          {currentStickers.map(sticker => (
            <button key={sticker.id}
              onClick={() => { onSelect(sticker); onClose(); }}
              title={sticker.name}
              style={{
                background: 'none', border: '2px solid transparent',
                borderRadius: 10, cursor: 'pointer', padding: 2,
                transition: 'transform 0.15s, border-color 0.15s'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = active; e.currentTarget.style.transform = 'scale(1.15)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'scale(1)'; }}>
              <img src={sticker.url} alt={sticker.name}
                style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 6, display: 'block' }} />
            </button>
          ))}
        </div>
      )}

      {!loading && !error && currentStickers.length === 0 && activePack && (
        <div style={{ padding: '20px', textAlign: 'center', color: sub, fontSize: 12 }}>No stickers in this pack yet.</div>
      )}
    </div>
  );
}
